import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, BookOpen } from "lucide-react";
import { api, fileUrl } from "@/lib/api";

const formatDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("pt-PT", { year: "numeric", month: "long", day: "numeric" }); }
    catch { return ""; }
};

/**
 * Shows up to 3 published blog posts tagged with the given city.
 * Renders nothing when there are no matching articles — so landings stay clean
 * until the blog has city-relevant content.
 */
export default function LandingBlogSection({ city }) {
    const [posts, setPosts] = useState([]);

    useEffect(() => {
        let cancelled = false;
        api.get("/blog").then((r) => {
            if (cancelled) return;
            const all = r.data || [];
            const target = (city || "").toLowerCase().trim();
            const matches = all.filter((p) =>
                (p.tags || []).some((t) => (t || "").toLowerCase().includes(target))
            );
            setPosts(matches.slice(0, 3));
        }).catch(() => {});
        return () => { cancelled = true; };
    }, [city]);

    if (!posts.length) return null;

    return (
        <section className="py-14 sm:py-20 bg-white" data-testid={`landing-blog-${city.toLowerCase()}`}>
            <div className="max-w-6xl mx-auto px-6 sm:px-10">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
                    <div>
                        <div className="eyebrow mb-6"><BookOpen className="w-3.5 h-3.5" /> Artigos sobre {city}</div>
                        <h2 className="font-display text-3xl sm:text-5xl font-bold tracking-tight leading-[1.05] max-w-3xl">
                            Ler antes de <span className="font-serif-italic text-[hsl(var(--copper))]">começar</span>.
                        </h2>
                    </div>
                    <Link to="/blog" className="btn-outline-dark">
                        Ver todos os artigos
                        <span className="arrow-circle"><ArrowUpRight className="w-3.5 h-3.5" /></span>
                    </Link>
                </div>

                <div className="grid md:grid-cols-3 gap-6">
                    {posts.map((p) => (
                        <Link key={p.id} to={`/blog/${p.slug}`} className="card-stone card-stone-hover flex flex-col overflow-hidden">
                            <div className="aspect-[16/10] bg-stone-100 overflow-hidden -m-6 mb-4">
                                {p.cover_id
                                    ? <img src={fileUrl(p.cover_id)} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                                    : <div className="w-full h-full bg-gradient-to-br from-[hsl(var(--forest))] to-[hsl(var(--forest-deep))] flex items-center justify-center"><span className="logo-mark text-6xl text-white/20">H<span className="dot">.</span></span></div>}
                            </div>
                            <h3 className="font-display text-lg font-bold text-stone-900 mb-2 leading-tight">{p.title}</h3>
                            {p.excerpt && <p className="text-stone-600 text-sm leading-relaxed line-clamp-3 mb-3">{p.excerpt}</p>}
                            <div className="mt-auto text-xs text-stone-500 pt-3 border-t border-stone-100 flex justify-between">
                                <span>{formatDate(p.published_at)}</span>
                                <span className="text-[hsl(var(--copper))] font-medium">Ler →</span>
                            </div>
                        </Link>
                    ))}
                </div>
            </div>
        </section>
    );
}
